import type { CSSProperties } from 'react'

import type { I18nT } from '@runtime/core/i18n/i18n'
import { COLOR_RGB, rgb, type TokenColorKey } from '@runtime/core/theme/colors'
import styles from '@runtime/features/profile/history/review/HistoryReviewSummary.module.scss'
import { msToClock } from '@runtime/shared/utils/time'

type HistoryReviewSummaryProps = {
  t: I18nT
  movesCount: number
  isPuzzleGame: boolean
  p1Name: string | null
  p2Name: string | null
  p1Color: string
  p2Color: string
  score1: number | null
  score2: number | null
  puzzleClicks: number | null
  puzzleTimeMs: number | null
  puzzleDotStyle: CSSProperties
  puzzlePlayerName: string
}

export default function HistoryReviewSummary({
  t,
  movesCount,
  isPuzzleGame,
  p1Name,
  p2Name,
  p1Color,
  p2Color,
  score1,
  score2,
  puzzleClicks,
  puzzleTimeMs,
  puzzleDotStyle,
  puzzlePlayerName,
}: HistoryReviewSummaryProps) {
  const resolvePlayerColor = (color: string) =>
    color in COLOR_RGB ? rgb(COLOR_RGB[color as TokenColorKey]) : '#666'

  return (
    <div className={styles.reviewMoves}>
      {isPuzzleGame ? (
        <div className={styles.reviewMovesHeader}>
          <div className={styles.reviewMovesPlayerRow}>
            <div className={styles.reviewMovesPlayerMain}>
              <span
                className={styles.reviewMovesPlayerDot}
                style={puzzleDotStyle}
              />
              <span className={styles.reviewMovesPlayerName}>
                {puzzlePlayerName}
              </span>
            </div>
            <span className={styles.reviewMovesPlayerScore}>
              {puzzleTimeMs != null && msToClock(puzzleTimeMs)}
              {puzzleClicks != null && ` · ${puzzleClicks} clics`}
            </span>
          </div>
        </div>
      ) : (
        (p1Name || p2Name) && (
          <div className={styles.reviewMovesHeader}>
            {p1Name && (
              <div className={styles.reviewMovesPlayerRow}>
                <div className={styles.reviewMovesPlayerMain}>
                  <span
                    className={styles.reviewMovesPlayerDot}
                    style={{
                      background: resolvePlayerColor(p1Color),
                    }}
                  />
                  <span className={styles.reviewMovesPlayerName}>{p1Name}</span>
                </div>
                {score1 != null && (
                  <span className={styles.reviewMovesPlayerScore}>
                    {score1}
                  </span>
                )}
              </div>
            )}

            {p2Name && (
              <div className={styles.reviewMovesPlayerRow}>
                <div className={styles.reviewMovesPlayerMain}>
                  <span
                    className={styles.reviewMovesPlayerDot}
                    style={{
                      background: resolvePlayerColor(p2Color),
                    }}
                  />
                  <span className={styles.reviewMovesPlayerName}>{p2Name}</span>
                </div>
                {score2 != null && (
                  <span className={styles.reviewMovesPlayerScore}>
                    {score2}
                  </span>
                )}
              </div>
            )}
          </div>
        )
      )}

      <div className={styles.reviewMovesTitle}>
        {t('profile.review.movesTitle')} ({movesCount})
      </div>
    </div>
  )
}
